import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { recreationFacilities, recreationCategories, recreationPrograms } from '../../mocks/recreation';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function RecreationPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedFacility, setExpandedFacility] = useState<number | null>(null);

  useSEO({
    title: 'Things to Do in Roseville CA - Activities, Entertainment & Recreation Guide',
    description: 'Discover fun things to do in Roseville California. Find activities for families, kids, adults, and seniors. Entertainment, recreation centers, sports, fitness, and free activities in Roseville CA.',
    keywords: 'things to do in Roseville CA, activities in Roseville California, family activities Roseville, free things to do Roseville, entertainment Roseville, recreation centers Roseville, fun in Roseville',
    canonical: '/recreation',
    ogImage: 'https://public.readdy.ai/ai/img_res/1593058108c03267d442afbca29fa690.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Recreation & Activities',
          description: 'Complete guide to recreational activities and entertainment in Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/recreation`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Recreation', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/recreation` },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What are the best things to do in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville offers a wide range of activities including visiting the Westfield Galleria, exploring 75+ parks and trails, swimming at aquatic centers, golfing at 3 courses, attending community events, and enjoying the local dining and brewery scene.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are there free things to do in Roseville California?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! Roseville has many free activities including hiking trails, community parks, free summer concerts, farmers markets, library events, and seasonal festivals. The city\'s 3,800 acres of parkland are free to enjoy year-round.',
              },
            },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredFacilities = selectedCategory === 'All'
    ? recreationFacilities
    : recreationFacilities.filter(f => f.category === selectedCategory);

  const featuredFacilities = recreationFacilities.filter(f => f.featured);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Home</Link>
              <Link to="/recreation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-teal-600' : 'text-teal-300'
              }`}>Recreation</Link>
              <Link to="/real-estate" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Real Estate</Link>
            </div>

            <Link to="/" className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
              Get Guide
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20beautiful%20California%20park%20with%20swimming%20pool%2C%20sports%20fields%2C%20families%20enjoying%20outdoor%20activities%2C%20green%20grass%2C%20blue%20sky%2C%20vibrant%20community%20recreation&width=1920&height=1080&seq=rechero001&orientation=landscape"
            alt="Roseville Recreation"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Parks & Recreation
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Discover world-class facilities, programs, and outdoor adventures in Roseville. 
            From aquatic centers to scenic trails, there's something for everyone.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#facilities" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer">
              Explore Facilities
            </a>
            <a href="#programs" className="bg-white/20 hover:bg-white/30 text-white px-8 py-3 rounded-full font-semibold transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer">
              View Programs
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-teal-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">75+</div>
              <div className="text-teal-100">Parks & Trails</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">5</div>
              <div className="text-teal-100">Aquatic Centers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">3</div>
              <div className="text-teal-100">Golf Courses</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">200+</div>
              <div className="text-teal-100">Annual Programs</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Facilities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Facilities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our most popular recreation destinations loved by residents and visitors alike
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredFacilities.map((facility) => (
              <div key={facility.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {facility.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 mb-2">{facility.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{facility.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-time-line mr-1"></i>
                    <span className="truncate">{facility.hours}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Facilities */}
      <section id="facilities" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">All Recreation Facilities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse our complete directory of parks, pools, sports facilities, and more
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {recreationCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Facilities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredFacilities.map((facility) => (
              <div
                key={facility.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {facility.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{facility.name}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{facility.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-map-pin-line text-teal-500 mt-0.5"></i>
                      <span>{facility.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-time-line text-teal-500"></i>
                      <span>{facility.hours}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-phone-line text-teal-500"></i>
                      <span>{facility.phone}</span>
                    </div>
                  </div>

                  {expandedFacility === facility.id && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Amenities:</h4>
                      <div className="flex flex-wrap gap-2">
                        {facility.amenities.map((amenity, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setExpandedFacility(expandedFacility === facility.id ? null : facility.id)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {expandedFacility === facility.id ? 'Show Less' : 'View Details'}
                    </button>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(facility.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-navigation-line"></i>
                      Directions
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recreation Programs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join our diverse programs designed for all ages and interests
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recreationPrograms.map((program) => (
              <div key={program.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100">
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${program.icon} text-teal-600 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{program.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{program.description}</p>
                <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                  {program.ageGroup}
                </span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>View All Programs</span>
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Active?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Register for programs, reserve facilities, or get your recreation pass today. 
            Join thousands of Roseville residents enjoying our world-class amenities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Register Online
            </a>
            <a
              href="tel:9167745950"
              className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Call (916) 774-5950
            </a>
          </div>
        </div>
      </section>

      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="text-2xl font-bold mb-4 block cursor-pointer">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                City of Roseville - Parks & Recreation
              </p>
              <p className="text-white/80 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, California 95747
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/recreation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Recreation</Link></li>
                <li><Link to="/real-estate" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Real Estate</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Recreation</h4>
              <ul className="space-y-2">
                <li><a href="#facilities" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Facilities</a></li>
                <li><a href="#programs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Programs</a></li>
                <li><a href="#" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Events</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <div className="flex space-x-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">
                © 2025 City of Roseville, California. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
